import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

interface PageSeo {
    title: string;
    description: string;
}

@Injectable({ providedIn: 'root' })
export class SeoService {
    private readonly renderer: Renderer2;
    private readonly schemaScriptId = 'devotory-jsonld';

    private readonly defaultSeo: PageSeo = {
        title: 'Devotory | Startup Tech Partner & Dedicated Dev Teams (USA)',
        description: 'Devotory builds MVPs, dedicated dev teams, and AI solutions for US startups and founders. Ship faster with a tech partner, not a vendor.'
    };

    private readonly pageSeo: Record<string, PageSeo> = {
        '': this.defaultSeo,
        'home-two': {
            title: 'Devotory Startup Technology Solutions | Home',
            description: 'Discover Devotory technology solutions for startups, including web development, dedicated teams, and AI expertise for ambitious founders.'
        },
        'home-three': {
            title: 'Devotory AI, Web Development & Startup Teams',
            description: 'Build and scale with Devotory, a startup technology partner delivering web applications, AI solutions, and dedicated development teams.'
        },
        'home-four': {
            title: 'Devotory Technology Partner for Growing Companies',
            description: 'Devotory helps growing companies turn ideas into dependable digital products with senior developers, modern web development, and AI.'
        },
        'about-us': {
            title: 'About Devotory | Startup Tech Partner Since 2014',
            description: 'Meet the team behind Devotory — a US-based tech partner helping startups build, scale, and ship with senior engineers and AI expertise.'
        },
        team: {
            title: 'Meet Devotory\'s Senior Technology Team',
            description: 'Meet Devotory\'s senior engineers and technology specialists who help startups and growing companies build and scale with confidence.'
        },
        services: {
            title: 'Web Development, AI & Dedicated Teams | Devotory Services',
            description: 'Explore Devotory\'s services: custom web development, dedicated development teams, and AI/ML solutions built for startups and growing companies.'
        },
        gallery: {
            title: 'Devotory Project Gallery | Technology Work',
            description: 'Browse selected Devotory projects and see how our teams deliver thoughtful digital products, web applications, and technology solutions.'
        },
        courses: {
            title: 'Technology Courses and Learning | Devotory',
            description: 'Explore Devotory courses and learning resources designed to help teams strengthen their technology skills and build better digital products.'
        },
        events: {
            title: 'Devotory Technology Events | Learn and Connect',
            description: 'Find upcoming Devotory technology events, talks, and opportunities to learn from experienced engineers and connect with the community.'
        },
        'case-studies-3-columns': {
            title: 'Devotory Case Studies | Startup Technology Results',
            description: 'See how Devotory partners with startups and growing companies to solve complex technology challenges and deliver measurable product results.'
        },
        'case-studies-details': {
            title: 'Devotory Case Study | Product Development Results',
            description: 'Explore a Devotory case study and learn how our technology teams turn complex product requirements into dependable business results.'
        },
        error: {
            title: 'Page Not Found | Devotory',
            description: 'The Devotory page you requested could not be found. Return to learn how our technology team can help with your next project.'
        },
        faq: {
            title: 'Frequently Asked Questions | Devotory',
            description: 'Get answers to common questions about Devotory services, dedicated development teams, web development, AI solutions, and project delivery.'
        },
        blog: {
            title: 'Devotory Technology Blog | Startup Insights',
            description: 'Read Devotory insights on web development, AI, product delivery, and building effective technology teams for startups and growing companies.'
        },
        'blog-details': {
            title: 'Devotory Insights | Technology and Startup Advice',
            description: 'Read practical Devotory guidance on web development, AI, product strategy, and building technology teams that help startups move forward.'
        },
        'courses-details': {
            title: 'Devotory Course Details | Technology Learning',
            description: 'Review Devotory course details and find focused technology learning resources for teams building modern digital products and services.'
        },
        'events-details': {
            title: 'Devotory Event Details | Technology Community',
            description: 'Get the details for Devotory technology events, including sessions and speakers designed to help teams learn, connect, and build better.'
        },
        contact: {
            title: 'Contact Devotory | Start Your Project',
            description: 'Talk to Devotory about your next build. Get a free consultation on web development, dedicated teams, or AI integration for your startup.'
        }
    };

    constructor(
        private readonly router: Router,
        private readonly title: Title,
        private readonly meta: Meta,
        rendererFactory: RendererFactory2,
        @Inject(DOCUMENT) private readonly document: Document
    ) {
        this.renderer = rendererFactory.createRenderer(null, null);
        this.router.events
            .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
            .subscribe(event => this.update(event.urlAfterRedirects));

        this.update(this.router.url);
    }

    private update(url: string): void {
        const path = url.split('?')[0].split('#')[0].replace(/^\//, '').replace(/\/$/, '');
        const seo = this.getSeo(path);

        this.title.setTitle(seo.title);
        this.meta.updateTag({ name: 'description', content: seo.description });
        this.updateStructuredData(path);
    }

    private updateStructuredData(path: string): void {
        const schemas = path === ''
            ? this.getHomepageSchemas()
            : this.getServiceSchema(path);

        const existingScript = this.document.getElementById(this.schemaScriptId);
        if (existingScript) {
            this.renderer.removeChild(this.document.head, existingScript);
        }

        if (!schemas.length) {
            return;
        }

        const script = this.renderer.createElement('script');
        this.renderer.setAttribute(script, 'id', this.schemaScriptId);
        this.renderer.setAttribute(script, 'type', 'application/ld+json');
        script.textContent = JSON.stringify({
            '@context': 'https://schema.org',
            '@graph': schemas
        });
        this.renderer.appendChild(this.document.head, script);
    }

    private getHomepageSchemas(): Record<string, unknown>[] {
        const organizationId = 'https://devotory.com/#organization';
        const organization = {
            '@type': 'Organization',
            '@id': organizationId,
            name: 'Devotory',
            url: 'https://devotory.com/',
            logo: 'https://devotory.com/assets/img/devotorylogo.png',
            sameAs: [
                'https://www.linkedin.com/company/devotory-solutions/',
                'https://github.com/devotory'
            ]
        };
        const localBusiness = {
            '@type': 'LocalBusiness',
            '@id': 'https://devotory.com/#localbusiness',
            name: 'Devotory',
            image: 'https://devotory.com/assets/img/devotorylogo.png',
            url: 'https://devotory.com/',
            telephone: '+1-209-500-1603',
            email: 'info@devotory.com',
            address: {
                '@type': 'PostalAddress',
                streetAddress: '30 N Gould Street',
                addressLocality: 'Sheridan',
                addressRegion: 'WY',
                postalCode: '82801',
                addressCountry: 'US'
            },
            parentOrganization: { '@id': organizationId }
        };
        const reviews = [
            {
                '@type': 'Review',
                author: { '@type': 'Person', name: 'Jamil Glenn' },
                reviewBody: "When it comes to creativity and innovation, Devotory truly stands out. Their team of highly skilled tech professionals brings unmatched expertise across a wide range of technology stacks. Devotory doesn't just deliver high-quality, end-to-end products - they go above and beyond by seamlessly integrating them into our systems and offering unparalleled customer support, even beyond project completion. ASA is exclusively powered by Devotory, and that partnership speaks volumes about their excellence. We couldn't ask for a better tech partner.",
                itemReviewed: { '@id': organizationId }
            },
            {
                '@type': 'Review',
                author: { '@type': 'Person', name: 'Sel' },
                reviewBody: "Working with Devotory has been a game-changer for our business. Their ability to understand complex requirements and transform them into scalable, cutting-edge solutions is truly impressive. What sets them apart is their commitment to collaboration and customer satisfaction. From brainstorming ideas to implementation and post-launch support, they were with us every step of the way. Their diverse expertise across various technologies allowed us to innovate faster and more efficiently than we thought possible. Devotory is more than a service provider - they're a partner in our success.",
                itemReviewed: { '@id': organizationId }
            }
        ];

        return [organization, localBusiness, ...reviews];
    }

    private getServiceSchema(path: string): Record<string, unknown>[] {
        if (!path.startsWith('services-details/')) {
            return [];
        }

        const serviceId = path.split('/')[1];
        const services: Record<string, Record<string, string>> = {
            '1': {
                name: 'Web Development',
                description: 'End-to-end web development across modern frameworks and technologies for startups and growing businesses.'
            },
            '2': {
                name: 'Dedicated Development Team',
                description: 'Dedicated senior developers who extend your in-house team with flexible engagement and reliable delivery.'
            },
            '3': {
                name: 'AI & ML Development',
                description: 'Custom artificial intelligence, machine learning, and large language model applications for business.'
            }
        };
        const service = services[serviceId];

        return service ? [{
            '@type': 'Service',
            '@id': `https://devotory.com/services-details/${serviceId}#service`,
            name: service.name,
            description: service.description,
            serviceType: service.name,
            provider: {
                '@type': 'Organization',
                name: 'Devotory',
                url: 'https://devotory.com/'
            },
            areaServed: 'Worldwide',
            url: `https://devotory.com/services-details/${serviceId}`
        }] : [];
    }

    private getSeo(path: string): PageSeo {
        if (path.startsWith('services-details/')) {
            const serviceId = path.split('/')[1];
            return this.getServiceSeo(serviceId);
        }

        return this.pageSeo[path] ?? this.pageSeo.error;
    }

    private getServiceSeo(serviceId: string): PageSeo {
        const serviceSeo: Record<string, PageSeo> = {
            '1': {
                title: 'Web Development Services for Startups | Devotory',
                description: 'Full-stack web development across modern frameworks. Devotory builds scalable, production-ready web apps for startups and growing businesses.'
            },
            '2': {
                title: 'Hire a Dedicated Development Team | Devotory',
                description: 'Get a dedicated team of senior developers at competitive rates — no hiring overhead, full ownership, and flexible scaling for your roadmap.'
            },
            '3': {
                title: 'AI & ML Development Services for Startups | Devotory',
                description: 'Devotory builds AI agents, LLM integrations, RAG pipelines, and copilots that give startups a real competitive edge — not just a chatbot bolt-on.'
            }
        };

        return serviceSeo[serviceId] ?? this.pageSeo.services;
    }
}
